// src/components/Sidebar/Sidebar.jsx
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Recycle, Users, Building2, FileBarChart, 
  Settings, LogOut, ChevronLeft, ChevronRight, Briefcase, 
  HeartHandshake, PlusCircle, UserCircle, BarChart, Activity, Mail, Bell, Target, X
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import toast from 'react-hot-toast';

export default function Sidebar({ isMobileOpen = false, onClose }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, logout, loading: authLoading, api } = useAuth();
  const { socket } = useSocket();
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [adminContactId, setAdminContactId] = useState(null);
  const navigate = useNavigate();
  const isDrawerVisible = Boolean(isMobileOpen);
  const desktopCollapsed = isCollapsed && !isDrawerVisible;

  // Debug (remove in production)
  // console.log('[Sidebar] user:', user, 'authLoading:', authLoading);

  if (authLoading) {
    return (
      <aside className="hidden md:flex bg-slate-900 text-white h-full w-72 flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-400 mb-3" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </aside>
    );
  }

  // If somehow no user in protected area → redirect handled by ProtectedRoute, but show fallback
  if (!user) {
    return (
      <aside className="hidden md:flex bg-slate-900 text-white h-full w-72 flex-col items-center justify-center p-6 text-center">
        <UserCircle className="h-14 w-14 text-red-400 mb-4" />
        <p className="text-slate-400 text-sm mb-4">Session expired or not authenticated</p>
        <button
          onClick={() => navigate('/login')}
          className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium"
        >
          Go to Login
        </button>
      </aside>
    );
  }

  const isNGO = user.role === 'ngo';
  const isVolunteer = user.role === 'volunteer';
  const isAgent = user.role === 'agent';

  const dashboardPath = user.role === 'admin' ? '/admin-dashboard' : user.role === 'ngo' ? '/ngo-dashboard' : user.role === 'volunteer' ? '/user-dashboard' : '/dashboard';

  const navItems = [
    // Always visible (role-specific dashboard path)
    { path: dashboardPath, label: 'Dashboard', icon: LayoutDashboard, exact: true },

    // Volunteer-specific
    ...(isVolunteer ? [
      { path: '/opportunities', label: 'Browse Opportunities', icon: Briefcase },
      { path: '/dashboard/my-applications', label: 'My Applications', icon: HeartHandshake },
      { path: '/dashboard/match-suggestions', label: 'Match Suggestions', icon: Target },
    ] : []),

    // NGO-specific
    ...(isNGO
      ? [
          { path: '/dashboard/opportunities', label: 'My Opportunities', icon: Briefcase },
          { path: '/dashboard/opportunities/create', label: 'Create New', icon: PlusCircle },
          { path: '/ngo/applications', label: 'Applications', icon: Users },
          {
            path: adminContactId
              ? `/chat/${adminContactId}?source=ngo-sidebar-admin-contact`
              : '/chat?source=ngo-sidebar-admin-contact',
            label: 'Contact Admin',
            icon: Mail,
          },
        ]
      : []),

    // Admin-specific
    ...(user.role === 'admin'
      ? [
          { path: '/admin-dashboard', label: 'Control Panel', icon: Settings, badge: true },
          { path: '/admin/users', label: 'User Management', icon: Users },
          { path: '/admin/opportunities', label: 'Opportunity Moderation', icon: Briefcase },
          { path: '/admin/reports', label: 'Reports & Exports', icon: FileBarChart },
          { path: '/admin/analytics', label: 'Analytics', icon: BarChart },
          // Use a distinct path for system health to avoid duplicate keys
          { path: '/admin/system-health', label: 'System Health', icon: Activity },
        ]
      : []),

    // Shared
    ...((isVolunteer || isNGO || isAgent)
      ? [{
          path: isAgent ? '/dashboard/assigned-pickups' : '/dashboard/pickups',
          label: isAgent ? 'Assigned Pickups' : isNGO ? 'Pickup Requests' : 'My Pickups',
          icon: Recycle,
        }]
      : []),
    // Only admins should see global volunteers/NGOs management
    ...(user.role === 'admin'
      ? [
          { path: '/dashboard/volunteers', label: 'Volunteers', icon: Users },
          { path: '/dashboard/ngos', label: 'NGOs', icon: Building2 },
          { path: '/dashboard/reports', label: 'Reports', icon: FileBarChart },
        ]
      : []),
    // messaging (all roles)
    { path: '/notifications', label: 'Notifications', icon: Bell },
    { path: '/chat', label: 'Messages', icon: Mail },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings },
  ];

  // Fetch small role-specific stats for sidebar badges and keep them live.
  useEffect(() => {
    let mounted = true;
    let refreshTimer = null;

    const getUnreadMessagesTotal = (res) => {
      const payload = res?.data || {};
      if (Number.isFinite(Number(payload.totalUnreadMessages))) {
        return Number(payload.totalUnreadMessages);
      }

      return (payload.conversations || []).reduce(
        (sum, item) => sum + Number(item?.unreadCount || 0),
        0
      );
    };

    const fetchStats = async () => {
      if (!user || !api) return;
      setStatsLoading(true);
      try {
        if (user.role === 'volunteer') {
          const [userRes, unreadRes, unreadMsgRes] = await Promise.all([
            api.get('/api/user/dashboard/stats').catch(() => ({ data: {} })),
            api.get('/api/notifications/unread-count').catch(() => ({ data: { unreadCount: 0 } })),
            api.get('/api/messages/conversations').catch(() => ({ data: { totalUnreadMessages: 0, conversations: [] } })),
          ]);
          if (!mounted) return;
          setStats({
            applications: userRes.data?.applications?.submitted || userRes.data?.applicationCount || 0,
            pickups: userRes.data?.pickups?.scheduled || 0,
            unread: unreadRes.data?.unreadCount ?? 0,
            unreadMessages: getUnreadMessagesTotal(unreadMsgRes),
          });
          setAdminContactId(null);
        } else if (user.role === 'ngo') {
          const [ngoRes, unreadRes, unreadMsgRes, adminRes] = await Promise.all([
            api.get('/api/ngo/dashboard/stats').catch(() => ({ data: {} })),
            api.get('/api/notifications/unread-count').catch(() => ({ data: { unreadCount: 0 } })),
            api.get('/api/messages/conversations').catch(() => ({ data: { totalUnreadMessages: 0, conversations: [] } })),
            api.get('/api/messages/admin-contacts').catch(() => ({ data: { admins: [] } })),
          ]);
          if (!mounted) return;

          const admins = adminRes.data?.admins || [];
          setAdminContactId(admins[0]?._id || null);

          setStats({
            pickups: ngoRes.data?.totalPickups || 0,
            opportunities: ngoRes.data?.opportunities?.total || ngoRes.data?.opportunityCount || 0,
            applications: ngoRes.data?.applications?.total || ngoRes.data?.applicationCount || 0,
            unread: unreadRes.data?.unreadCount ?? 0,
            unreadMessages: getUnreadMessagesTotal(unreadMsgRes),
          });
        } else if (user.role === 'agent') {
          const [agentRes, unreadRes, unreadMsgRes] = await Promise.all([
            api.get('/api/agent/pickups').catch(() => ({ data: {} })),
            api.get('/api/notifications/unread-count').catch(() => ({ data: { unreadCount: 0 } })),
            api.get('/api/messages/conversations').catch(() => ({ data: { totalUnreadMessages: 0, conversations: [] } })),
          ]);
          if (!mounted) return;
          setStats({
            pickups: agentRes.data?.pickups?.length || 0,
            unread: unreadRes.data?.unreadCount ?? 0,
            unreadMessages: getUnreadMessagesTotal(unreadMsgRes),
          });
          setAdminContactId(null);
        } else if (user.role === 'admin') {
          const [adminRes, unreadRes, unreadMsgRes] = await Promise.all([
            api.get('/api/admin/dashboard/stats').catch(() => ({ data: {} })),
            api.get('/api/notifications/unread-count').catch(() => ({ data: { unreadCount: 0 } })),
            api.get('/api/messages/conversations').catch(() => ({ data: { totalUnreadMessages: 0, conversations: [] } })),
          ]);
          if (!mounted) return;
          setStats({
            users: adminRes.data?.users || 0,
            reports: adminRes.data?.reports || 0,
            unread: unreadRes.data?.unreadCount ?? 0,
            unreadMessages: getUnreadMessagesTotal(unreadMsgRes),
          });
          setAdminContactId(null);
        }
      } catch (err) {
        console.warn('[Sidebar] fetchStats failed', err);
      } finally {
        if (mounted) setStatsLoading(false);
      }
    };

    const scheduleStatsRefresh = (delay = 120) => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      refreshTimer = setTimeout(() => {
        fetchStats();
        refreshTimer = null;
      }, delay);
    };

    fetchStats();

    const intervalId = setInterval(() => {
      fetchStats();
    }, 30000);

    const handleRealtimeUpdate = () => scheduleStatsRefresh();
    const handleReadUpdate = () => scheduleStatsRefresh(0);

    window.addEventListener('focus', handleReadUpdate);
    window.addEventListener('chat:read-updated', handleReadUpdate);
    window.addEventListener('notifications:updated', handleReadUpdate);

    if (socket) {
      socket.on('message:receive', handleRealtimeUpdate);
      socket.on('notification:receive', handleRealtimeUpdate);
    }

    return () => {
      mounted = false;
      clearInterval(intervalId);
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      window.removeEventListener('focus', handleReadUpdate);
      window.removeEventListener('chat:read-updated', handleReadUpdate);
      window.removeEventListener('notifications:updated', handleReadUpdate);

      if (socket) {
        socket.off('message:receive', handleRealtimeUpdate);
        socket.off('notification:receive', handleRealtimeUpdate);
      }
    };
  }, [user, api, socket]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (err) {
      toast.error('Logout failed – try again');
      console.error('Logout error:', err);
    }
  };

  return (
    <>
      {isDrawerVisible && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/45 backdrop-blur-[1px] md:hidden"
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex h-full flex-col overflow-hidden
          bg-slate-900 text-white
          border-r border-slate-800
          shadow-xl shadow-black/30 transition-all duration-300 ease-in-out
          ${desktopCollapsed ? 'w-[72px]' : 'w-64'}
          ${isDrawerVisible ? 'translate-x-0' : '-translate-x-full'}
          md:static md:translate-x-0 md:z-20
        `}
      >
      {/* Logo + Collapse Toggle */}
      <div className="flex h-16 items-center justify-between border-b border-slate-800 px-4">
        {!desktopCollapsed && (
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
              <Recycle className="h-5 w-5 text-emerald-400" />
            </div>
            <span className="text-base font-bold tracking-tight text-white">WasteZero</span>
          </div>
        )}
        {desktopCollapsed && (
          <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
            <Recycle className="h-5 w-5 text-emerald-400" />
          </div>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus:outline-none md:inline-flex"
            aria-label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {desktopCollapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white md:hidden"
            aria-label="Close sidebar"
          >
            <X size={17} />
          </button>
        </div>
      </div>

      {/* Navigation Links */}
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-slate-700">
        {!desktopCollapsed && (
          <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Navigation
          </p>
        )}
        {navItems.map((item) => {
          const path = item.path || '';
          let badgeCount = 0;
          if (path.includes('applications')) badgeCount = stats.applications || 0;
          else if (path.includes('/opportunities') && user?.role === 'ngo') badgeCount = stats.opportunities || 0;
          else if (path.includes('pickups')) badgeCount = stats.pickups || 0;
          else if (path.includes('reports')) badgeCount = stats.reports || 0;
          else if (path.includes('volunteers')) badgeCount = stats.users || 0;
          else if (path.includes('notifications')) badgeCount = stats.unread || 0;
          else if (path.includes('/chat')) badgeCount = stats.unreadMessages || 0;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={() => {
                if (isDrawerVisible && onClose) onClose();
              }}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-150
                ${
                  isActive
                    ? 'bg-emerald-500/10 text-emerald-400 font-medium'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                }`
              }
              title={desktopCollapsed ? item.label : undefined}
            >
              {({ isActive }) => (
                <>
                  {/* Left accent bar */}
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-emerald-500" />
                  )}
                  <item.icon className={`h-4 w-4 shrink-0 ${desktopCollapsed ? 'mx-auto' : ''}`} />
                  {!desktopCollapsed && <span className="truncate">{item.label}</span>}

                  {/* Badge */}
                  {badgeCount > 0 && !desktopCollapsed && (
                    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10px] font-semibold text-white">
                      {badgeCount}
                    </span>
                  )}
                  {badgeCount > 0 && desktopCollapsed && (
                    <span className="absolute -right-0.5 top-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-[9px] font-semibold text-white">
                      {badgeCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-slate-800 p-3">
        <button
          onClick={() => navigate('/profile')}
          className="mb-2 flex w-full items-center gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-slate-800 focus:outline-none"
          title={desktopCollapsed ? 'View / Edit Profile' : undefined}
        >
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-slate-700 bg-linear-to-br from-emerald-600 to-teal-700 flex items-center justify-center font-semibold text-sm shadow-sm">
            {user.profilePicture ? (
              <img
                src={user.profilePicture.startsWith('http') ? user.profilePicture : `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${user.profilePicture}`}
                alt={user.name || 'Profile'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=10b981&color=fff&size=128`;
                }}
              />
            ) : (
              (user.name?.[0] || 'U').toUpperCase()
            )}
          </div>

          {!desktopCollapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-100">
                {user.name || 'User'}
              </p>
              <p className="truncate text-xs text-slate-500">
                {user.email || 'No email'}
              </p>
              <span className="mt-1 inline-block rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium capitalize text-emerald-400">
                {user.role}
              </span>
            </div>
          )}
        </button>

        <button
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5
                     bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400
                     transition-all duration-200 text-sm font-medium focus:outline-none"
        >
          <LogOut className="h-4 w-4" />
          {!desktopCollapsed && <span>Sign out</span>}
        </button>
      </div>
      </aside>
    </>
  );
}